import { DataGridPremiumProps } from '@mui/x-data-grid-premium';
import { useSnackbar } from 'notistack';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SWRConfiguration } from 'swr';
import * as yup from 'yup';

import {
  DataGridCRUDConfig,
  useDataGridCRUDValidation,
  useDataGridRowFileChangesState,
} from '~/components/SolaForceDataGrid/hooks';
import { EnumListKey, useAllEnums } from '~/data/enums';
import {
  deleteEquipment,
  saveEmployeeEquipment,
  useGetEmployeeEquipment,
} from '~/data/equipment';
import { useGetCompanyEquipment } from '~/data/equipment/useGetCompanyEquipment';
import { useGetNextEquipmentNumber } from '~/data/equipment/useGetNextEquipmentNumber';
import { Roles } from '~/data/user';
import { useSelectedEmployeeId } from '~/hooks/employee';
import {
  EmployeeEquipmentEntry,
  EmployeeEquipmentTableRow,
  EquipmentEntryType,
  convertCompanyEquipmentTypeToEquipmentEntry,
  convertEmployeeEquipmentTableRowToSubmission,
  convertEmployeeEquipmentTypeToEquipmentEntry,
  employeeEquipmentSubmissionToFormData,
} from '~/scenes/PeopleModule/components/Tabs/EquipmentTab/EquipmentTable/data';
import { UseNewEquipmentDialogProps } from '~/scenes/PeopleModule/components/Tabs/EquipmentTab/EquipmentTable/NewEquipmentDialog/useNewEquipmentDialog';
import { UseEquipmentTableColumnsProps } from '~/scenes/PeopleModule/components/Tabs/EquipmentTab/EquipmentTable/useEquipmentTableColumns';

type UseEquipmentTableOutput = {
  isLoading: boolean;
  dataGridProps: Omit<
    DataGridPremiumProps<EmployeeEquipmentTableRow>,
    'columns'
  >;
  crudConfig: DataGridCRUDConfig<EmployeeEquipmentTableRow, number>;
  columnsConfig: UseEquipmentTableColumnsProps;
  newEquipmentDialog: Omit<UseNewEquipmentDialogProps, 'onAddNewRow'>;
};

export const useEquipmentTable = (): UseEquipmentTableOutput => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const {
    selectedEmployeeId,
    selectedEmployeeDetails: { hasRoleAccessOverEmployee, isMyselfWithRole },
  } = useSelectedEmployeeId();

  const userAccess = useMemo((): Pick<
    UseEquipmentTableColumnsProps,
    'canEdit' | 'canAlwaysEditExpirationDate'
  > => {
    return {
      canEdit:
        hasRoleAccessOverEmployee([
          Roles.HR_ADMIN,
          Roles.MANAGER,
          Roles.ALT_MANAGER,
          Roles.IT_ADMIN,
        ]) ||
        isMyselfWithRole([
          Roles.MANAGER,
          Roles.HR_ADMIN,
          Roles.HR_SALARY,
          Roles.HR_DEV,
          Roles.HR_STAFF,
          Roles.HR_PEOP_DEV,
          Roles.HR_PEOP,
          Roles.HR_ADMIN_LIMITED,
        ]),
      canAlwaysEditExpirationDate: hasRoleAccessOverEmployee([Roles.HR_ADMIN]),
    };
  }, [hasRoleAccessOverEmployee, isMyselfWithRole]);

  const swrConfig: SWRConfiguration = {
    revalidateOnFocus: false,
  };

  const { getEnums } = useAllEnums();
  const { equipmentTypes } = useMemo(() => {
    return {
      equipmentTypes: getEnums(EnumListKey.EQUIPMENT_TYPE),
    };
  }, [getEnums]);

  const {
    data: companyEquipmentData,
    isLoading: isLoadingCompanyEquipment,
    mutate: mutateCompanyEquipment,
  } = useGetCompanyEquipment(swrConfig);

  const { companyEquipment } = useMemo(() => {
    return {
      companyEquipment: companyEquipmentData.map(
        convertCompanyEquipmentTypeToEquipmentEntry,
      ),
    };
  }, [companyEquipmentData]);

  const {
    data: employeeEquipmentData,
    isLoading: isLoadingEmployeeEquipment,
    mutate: mutateEmployeeEquipment,
  } = useGetEmployeeEquipment(
    {
      employeeId: selectedEmployeeId,
    },
    swrConfig,
  );

  const { employeeEquipment } = useMemo(() => {
    return {
      employeeEquipment: employeeEquipmentData.map(
        convertEmployeeEquipmentTypeToEquipmentEntry,
      ),
    };
  }, [employeeEquipmentData]);

  const {
    data: nextEquipmentNumber,
    updateUsedEquipmentNumber,
    isLoading: isLoadingNextEquipmentNumber,
    mutate: mutateNextEquipmentNumber,
  } = useGetNextEquipmentNumber(swrConfig);

  const { fileChanges, updateFileChanges, resetFileChange } =
    useDataGridRowFileChangesState();

  const validationSchema = yup.object().shape<EmployeeEquipmentTableRow>({
    id: yup.number(),
    entryType: yup.string(),
    type: yup
      .string()
      .required(t('laFillManFields'))
      .oneOf(
        equipmentTypes.map((type) => type.code),
        t('laFillManFields'),
      ),
    item: yup.string(),
    deliveryDate: yup.date().required(t('laFillManFields')),
    returnDate: yup.date().max(new Date(), t('laErrorEquipReDateNotFuture')),
    companyEquipmentId: yup.number(),
    inventoryNumber: yup.string(),
    info: yup.string(),
    attachmentName: yup.string(),
    attachmentId: yup.number(),
    attachmentIsArchived: yup.boolean(),
    expirationDate: yup.date(),
  });

  const includeFileChangesToSubmission = useCallback(
    (submitRow: EmployeeEquipmentTableRow): EmployeeEquipmentTableRow => {
      const fileChange = fileChanges.find((fc) => fc.id === submitRow.id);
      if (!!fileChange) {
        const { file } = fileChange;

        return !!file
          ? {
              ...submitRow,
              attachment: file,
              attachmentId: -1,
              attachmentName: file?.name ?? '',
              attachmentIsArchived: false,
            }
          : {
              ...submitRow,
              attachmentId: -1,
              attachmentName: '',
              attachmentIsArchived: false,
            };
      }

      return submitRow;
    },
    [fileChanges],
  );

  const handleSubmitPromise = useCallback(
    async <T>(promise: Promise<T>) => {
      return promise
        .then((result) => {
          mutateCompanyEquipment();
          mutateEmployeeEquipment();
          mutateNextEquipmentNumber();
          enqueueSnackbar(t('change_saved'), { variant: 'success' });
          return Promise.resolve(result);
        })
        .catch((error) => {
          enqueueSnackbar(t('laFailedToSave'), { variant: 'error' });
          return Promise.reject(error);
        });
    },
    [
      t,
      enqueueSnackbar,
      mutateCompanyEquipment,
      mutateEmployeeEquipment,
      mutateNextEquipmentNumber,
    ],
  );

  // Handle Add New & Update
  const handleSubmit = useCallback(
    async (
      submitRow: EmployeeEquipmentTableRow,
      method: 'POST' | 'UPDATE',
    ): Promise<EmployeeEquipmentEntry> => {
      const submitData: EmployeeEquipmentTableRow =
        includeFileChangesToSubmission(submitRow);

      return validationSchema
        .validate(submitData, { abortEarly: false })
        .then(async () => {
          const isUpdate = method === 'UPDATE';
          const submission =
            convertEmployeeEquipmentTableRowToSubmission(submitData);

          return handleSubmitPromise(
            saveEmployeeEquipment(
              selectedEmployeeId,
              isUpdate ? submission.fEquipmentId : undefined,
              employeeEquipmentSubmissionToFormData(submission),
              submission.fEquipmentCompEqFK === -1,
            ),
          ).then((result) => {
            resetFileChange(submitRow.id);
            return Promise.resolve(
              convertEmployeeEquipmentTypeToEquipmentEntry(result),
            );
          });
        })
        .catch((err) => {
          return Promise.reject({ rowId: submitRow.id, error: err });
        });
    },
    [
      validationSchema,
      includeFileChangesToSubmission,
      handleSubmitPromise,
      selectedEmployeeId,
      resetFileChange,
    ],
  );

  // Handle Delete
  const onSubmitDelete = useCallback(
    async (deletedRow: EmployeeEquipmentTableRow) => {
      return handleSubmitPromise(
        deleteEquipment(selectedEmployeeId, deletedRow.id),
      ).then(() => {
        resetFileChange(deletedRow.id);
      });
    },
    [handleSubmitPromise, selectedEmployeeId, resetFileChange],
  );

  const { actionsValidation, columnsValidation, dataGridProps } =
    useDataGridCRUDValidation<EmployeeEquipmentTableRow>({
      validationSchema,
    });

  const crudConfig: DataGridCRUDConfig<EmployeeEquipmentTableRow, number> = {
    data: {
      onSubmitCreate: (createdRow: EmployeeEquipmentTableRow) =>
        handleSubmit(createdRow, 'POST'),
      onSubmitUpdate: (updatedRow: EmployeeEquipmentTableRow) =>
        handleSubmit(updatedRow, 'UPDATE'),
      onSubmitDelete,
      onCancelEdit: (editedRow) => {
        resetFileChange(editedRow.id);
      },
    },
    getRowId: (row) => row.id,
    addNewRow: {
      getNewId: (rows) => rows.length * -1,
      getInitialRowData: (
        newId: EmployeeEquipmentTableRow['id'],
      ): EmployeeEquipmentTableRow => {
        return {
          id: newId,
          entryType: EquipmentEntryType.Employee,
          inventoryNumber: nextEquipmentNumber ?? '',
          type: '',
          companyEquipmentId: -1,
          item: '',
          deliveryDate: new Date(),
          attachmentId: -1,
          attachmentName: '',
          attachmentIsArchived: false,
        };
      },
      fieldToFocus: 'deliveryDate',
    },
    isRowEditable: () => {
      return true;
    },
    getDeleteRowMessage: (row) => {
      return `${t('delete')}: ${row.inventoryNumber}`;
    },
    actionsValidation,
    permissions: {
      canAddNew: userAccess.canEdit,
    },
  };

  return {
    isLoading:
      isLoadingCompanyEquipment ||
      isLoadingEmployeeEquipment ||
      isLoadingNextEquipmentNumber,
    dataGridProps: {
      ...dataGridProps,
      rows: employeeEquipment,
    },
    columnsConfig: {
      selectedEmployeeId,
      equipmentTypes,
      companyEquipment,
      columnsValidation,
      fileChanges,
      updateFileChanges,
      ...userAccess,
    },
    crudConfig,
    newEquipmentDialog: {
      equipmentTypes,
      companyEquipment,
      nextEquipmentNumber: nextEquipmentNumber ?? '',
      updateUsedEquipmentNumber,
    },
  };
};
